import React from "react";
import { Link } from "react-router-dom";
import { MdPerson, MdHandshake } from "react-icons/md";
import LegalFooterBannerAndLinks from "components/legal/LegalFooterBannerAndLinks";
import LegalPagesBrandHeader from "components/legal/LegalPagesBrandHeader";

/**
 * Subscriber-facing “How it works” — step-by-step + dual onboarding paths.
 * Canonical URL: `/HowItWorks`. Broker reference remains at `/hiw`.
 */

function FlowArrow() {
  return (
    <div className="flex justify-center py-1 text-brand-400" aria-hidden>
      <span className="text-lg font-light leading-none">↓</span>
    </div>
  );
}

function PathStep({ n, title, detail }) {
  return (
    <div className="flex gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-500/25 text-sm font-bold text-brand-200">
        {n}
      </span>
      <div>
        <p className="font-semibold text-white">{title}</p>
        {detail ? <p className="mt-1 text-xs text-gray-400">{detail}</p> : null}
      </div>
    </div>
  );
}

function MinimumRequiredNote() {
  return (
    <div className="mt-10 rounded-2xl border border-amber-500/35 bg-amber-500/10 p-6 sm:p-7">
      <h3 className="text-sm font-bold uppercase tracking-wide text-amber-200">
        Minimum required
      </h3>
      <p className="mt-4 text-sm font-semibold text-white">
        Minimum required Leverage 1:1000 = USD 200.
      </p>
    </div>
  );
}

export default function HowItWorksFlow() {
  return (
    <main className="min-h-screen bg-[#0B1437] px-5 py-6 text-white sm:py-8">
      <div className="mx-auto w-full max-w-5xl">
        <LegalPagesBrandHeader />
        <header className="text-center">
          <h1 className="text-3xl font-bold sm:text-4xl">
            <span className="block">How it works</span>
            <span className="mt-2 block sm:mt-3">
              step-by-step for subscribers
            </span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-gray-300 sm:text-base">
            Everyone starts with Sign up. Then pick how you connect your broker—your personal
            broker, or our broker partner—and follow the matching steps.
          </p>
        </header>

        <section className="relative mt-16 flex justify-center">
          <div className="flex w-full max-w-md flex-col items-center justify-center rounded-3xl border border-white/10 bg-gradient-to-br from-brand-500/30 to-brand-600/10 p-7 py-9 shadow-[0_20px_50px_rgba(0,0,0,0.35)] sm:p-10 sm:py-10">
            <div className="w-full max-w-sm rounded-xl border border-white/20 bg-white/[0.06] px-5 py-5 text-center sm:px-7 sm:py-6">
              <p className="text-base font-semibold text-white sm:text-lg">
                Sign up &amp; verify your account
              </p>
              <p className="mt-2 text-xs leading-relaxed text-gray-400">
                Create your AlphaSniper account and sign in. Your slots, plans, and
                trading-account rows are tied to this profile.
              </p>
            </div>
          </div>
        </section>

        {/* Choose your path */}
        <section className="mt-20">
          <h2 className="text-center text-2xl font-bold sm:text-3xl">Choose your path</h2>
          <p className="mx-auto mt-2 max-w-xl text-center text-sm text-gray-400">
            Same end goal—an active slot and copy execution—but the order of broker checks differs.
          </p>

          <div className="mt-10 grid gap-8 lg:grid-cols-2 lg:gap-10">
            {/* Path 1 — broker partner */}
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6 sm:p-8">
              <div className="flex items-center gap-3 border-b border-white/10 pb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500/20 text-emerald-200">
                  <MdHandshake className="h-7 w-7" aria-hidden />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-emerald-300">
                    Path 1
                  </p>
                  <h3 className="text-lg font-bold text-white">Use our broker partner</h3>
                </div>
              </div>
              <div className="mt-6 space-y-0">
                <PathStep
                  n={1}
                  title="Verify & fund your account"
                  detail="Complete partner-broker verification and deposit the minimum required before we activate copy."
                />
                <FlowArrow />
                <PathStep n={2} title="Buy a slot" detail="Subscribe to your chosen tier and promo (partner path)." />
                <FlowArrow />
                <PathStep
                  n={3}
                  title="Add your trading account"
                  detail="Connect the verified, funded account to your slot for provisioning."
                />
                <FlowArrow />
                <PathStep n={4} title="Start earning" detail="Copy goes live when the slot is active and rules are met." />
              </div>
              <div className="mt-6 rounded-xl border border-emerald-500/40 bg-emerald-950/25 p-4">
                <p className="text-xs font-bold uppercase tracking-wide text-emerald-200">
                  Refund policy
                </p>
                <p className="mt-2 text-sm leading-relaxed text-gray-200">
                  If you use <strong className="text-white">our broker partner</strong>, you may be{" "}
                  <strong className="text-white">eligible</strong> for the first-month refund when
                  all conditions in the{" "}
                  <Link to="/RefundPolicy" className="font-semibold text-brand-300 hover:underline">
                    Refund Policy
                  </Link>{" "}
                  are met (automatic trading only, timing of requests, etc.).
                </p>
              </div>
            </div>

            {/* Path 2 — personal broker */}
            <div className="rounded-2xl border border-sky-500/30 bg-sky-500/5 p-6 sm:p-8">
              <div className="flex items-center gap-3 border-b border-white/10 pb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-500/20 text-sky-200">
                  <MdPerson className="h-7 w-7" aria-hidden />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-sky-300">
                    Path 2
                  </p>
                  <h3 className="text-lg font-bold text-white">Use your personal broker</h3>
                </div>
              </div>
              <p
                className="mt-6 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-center text-sm font-semibold text-amber-100"
                role="status"
              >
                This path is temporarily disabled.
              </p>
              <div className="mt-6 space-y-0">
                <PathStep
                  n={1}
                  title="Buy a slot"
                  detail="Pick your tier and complete subscription for your slot."
                />
                <FlowArrow />
                <PathStep
                  n={2}
                  title="Add your trading account"
                  detail="Link your account with the minimum deposit required by your broker and our onboarding rules."
                />
                <FlowArrow />
                <PathStep n={3} title="Start earning" detail="Once active, copy runs according to your risk settings." />
              </div>
              <div className="mt-6 rounded-xl border border-red-500/30 bg-red-950/30 p-4">
                <p className="text-xs font-bold uppercase tracking-wide text-red-200">
                  Refund policy
                </p>
                <p className="mt-2 text-sm leading-relaxed text-gray-200">
                  If you use <strong className="text-white">your personal broker</strong>, you are{" "}
                  <strong className="text-white">not eligible</strong> for our first-month refund
                  offer. See the{" "}
                  <Link to="/RefundPolicy" className="font-semibold text-brand-300 hover:underline">
                    Refund Policy
                  </Link>{" "}
                  for full terms.
                </p>
              </div>
            </div>
          </div>

          <MinimumRequiredNote />
        </section>

        <section className="mt-20 rounded-2xl border border-white/10 bg-white/[0.04] p-8 text-center">
          <h2 className="text-lg font-bold sm:text-xl">Ready to start?</h2>
          <p className="mx-auto mt-2 max-w-lg text-sm text-gray-400">
            Sign up first, then open Pricing and Subscription to match your path.
          </p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/SignUp"
              className="inline-flex rounded-xl bg-brand-500 px-6 py-3 text-sm font-bold text-white hover:bg-brand-600"
            >
              Sign up
            </Link>
            <Link
              to="/Pricing"
              className="inline-flex rounded-xl border border-white/25 bg-white/5 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10"
            >
              View pricing
            </Link>
          </div>
        </section>

        <footer className="mt-8 border-t border-white/10 pt-5 text-center text-xs text-gray-400 sm:text-sm">
          <p>AlphaSniper 2026 © All Rights Reserved</p>
          <LegalFooterBannerAndLinks />
        </footer>
      </div>
    </main>
  );
}
